// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.

import { useState, useEffect } from 'react';
import { ArrowUpFromLine, AlertCircle, Clock, Loader2, Info, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { applicationAPI, slaAPI } from '../services/api';
import { toast } from 'sonner';

export default function EscalationPage() {
  const [apps, setApps] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', l2_threshold_percent: 50, l3_threshold_percent: 75, l4_threshold_percent: 90 });

  const fetch = async () => {
    setLoading(true);
    try {
      const [aRes, pRes] = await Promise.all([applicationAPI.list(), slaAPI.listEscalationPolicies()]);
      setApps((aRes.data?.results || aRes.data || []).filter(a => a.approval_levels > 0));
      setPolicies(pRes.data?.results || pRes.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editing) { await slaAPI.updateEscalationPolicy(editing, form); toast.success('Updated'); }
      else { await slaAPI.createEscalationPolicy(form); toast.success('Created'); }
      setShowForm(false); setEditing(null); fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete policy?')) return;
    try { await slaAPI.deleteEscalationPolicy(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed — policy may be in use'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  return (
    <div data-testid="escalation-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2"><ArrowUpFromLine className="w-6 h-6 text-orange-600" /> Escalation Matrix</h2>
          <p className="text-sm text-slate-500 mt-1">Create escalation policies and assign them to applications</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', l2_threshold_percent: 50, l3_threshold_percent: 75, l4_threshold_percent: 90 }); }}
          className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600"><Plus className="w-4 h-4" /> Create Policy</button>
      </div>

      <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-xl border border-orange-200 dark:border-orange-800 mb-6 flex items-start gap-3">
        <Info className="w-5 h-5 text-orange-700 dark:text-orange-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-orange-900 dark:text-orange-300">
          <p><strong>How it works:</strong> Create escalation policies with custom thresholds, then assign a policy to each application in Applications / Systems page. Escalation targets are auto-derived from the application's approval chain (Team Lead → App Manager → App Head).</p>
          <p className="mt-1">Applications with no policy or escalation disabled will not trigger escalations.</p>
        </div>
      </div>

      {/* Create/Edit Policy */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Escalation Policy</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <input placeholder="Policy Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <div><label className="text-xs text-slate-500">L2 Threshold (%)</label><input type="number" value={form.l2_threshold_percent} onChange={e => setForm({...form, l2_threshold_percent: parseInt(e.target.value)||0})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" /></div>
            <div><label className="text-xs text-slate-500">L3 Threshold (%)</label><input type="number" value={form.l3_threshold_percent} onChange={e => setForm({...form, l3_threshold_percent: parseInt(e.target.value)||0})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" /></div>
            <div><label className="text-xs text-slate-500">L4 Threshold (% or 0=off)</label><input type="number" value={form.l4_threshold_percent} onChange={e => setForm({...form, l4_threshold_percent: parseInt(e.target.value)||0})} className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
          </div>
        </div>
      )}

      {/* Policies Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm mb-6">
        <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-b dark:border-slate-700">
          <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2"><Clock className="w-5 h-5 text-orange-600" /> Escalation Policies</h3>
        </div>
        <table className="w-full">
          <thead className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Policy Name</th>
              <th className="px-4 py-3 text-center font-semibold">L2 → Team Lead</th>
              <th className="px-4 py-3 text-center font-semibold">L3 → App Manager</th>
              <th className="px-4 py-3 text-center font-semibold">L4 → App Head</th>
              <th className="px-4 py-3 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-slate-700 text-sm">
            {policies.map(p => (
              <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                <td className="px-4 py-3"><span className="font-medium dark:text-white">{p.name}</span>{p.description && <span className="text-xs text-slate-400 ml-2">{p.description}</span>}</td>
                <td className="px-4 py-3 text-center"><span className="text-amber-600 font-bold">{p.l2_threshold_percent}%</span></td>
                <td className="px-4 py-3 text-center"><span className="text-orange-600 font-bold">{p.l3_threshold_percent}%</span></td>
                <td className="px-4 py-3 text-center">{p.l4_threshold_percent > 0 ? <span className="text-rose-600 font-bold">{p.l4_threshold_percent}%</span> : <span className="text-slate-400">Disabled</span>}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-1">
                    <button onClick={() => { setForm({ name: p.name, description: p.description || '', l2_threshold_percent: p.l2_threshold_percent, l3_threshold_percent: p.l3_threshold_percent, l4_threshold_percent: p.l4_threshold_percent }); setEditing(p.id); setShowForm(true); }} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {policies.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No escalation policies. Create one above.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Applications & Their Escalation Config */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm">
        <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 border-b dark:border-slate-700">
          <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2"><AlertCircle className="w-5 h-5 text-sky-500" /> Applications & Escalation Targets</h3>
        </div>
        <table className="w-full">
          <thead className="text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Application</th>
              <th className="px-4 py-3 text-center font-semibold">Escalation Policy</th>
              <th className="px-4 py-3 text-center font-semibold">Enabled</th>
              <th className="px-4 py-3 text-left font-semibold">L2 → Team Lead</th>
              <th className="px-4 py-3 text-left font-semibold">L3 → App Manager</th>
              <th className="px-4 py-3 text-left font-semibold">L4 → App Head</th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-slate-700 text-sm">
            {apps.map(app => (
              <tr key={app.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                <td className="px-4 py-3 font-medium dark:text-white">{app.name}</td>
                <td className="px-4 py-3 text-center">{app.escalation_policy_name ? <span className="bg-sky-100 text-sky-800 px-2 py-0.5 rounded text-xs">{app.escalation_policy_name}</span> : <span className="text-slate-400 text-xs">None</span>}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs ${app.escalation_enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{app.escalation_enabled !== false ? 'ON' : 'OFF'}</span></td>
                <td className="px-4 py-3 dark:text-slate-300">{app.app_team_lead_name || <span className="text-slate-400">-</span>}</td>
                <td className="px-4 py-3 dark:text-slate-300">{app.app_manager_name || <span className="text-slate-400">-</span>}</td>
                <td className="px-4 py-3 dark:text-slate-300">{app.app_head_name || <span className="text-slate-400">-</span>}</td>
              </tr>
            ))}
            {apps.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No applications configured</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
